import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent, { DataTypeProps } from "../../components/TableComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import { createColumn } from "../../utils/tableUtils";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
	StatusCell
} from "../../components/styled/common";
import { useVolunteersExperienceContext } from "../../context/VolunteersExperienceContext/VolunteersExperienceContext";
import { VOLUNTEERS_EXPERIENCE_LIST_PATH, VolunteersExperienceListItem } from "../../context/VolunteersExperienceContext/constants";

// The table shows, sorts and searches the name and the surname together.
type VolunteersExperienceRow = VolunteersExperienceListItem & { fullName: string };

const VolunteersExperienceList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<VolunteersExperienceRow | null>(null);
	const {
		volunteersExperienceList,
		clearVolunteersExperienceList,
		clearVolunteersExperienceFormData,
		getVolunteersExperienceList,
		removeVolunteersExperience,
		operationStatus,
	} = useVolunteersExperienceContext();

	const [searchText, setSearchText] = useState("");

	const navigate = useNavigate();

	const rows = useMemo<VolunteersExperienceRow[]>(
		() => volunteersExperienceList.map(item => ({ ...item, fullName: `${item.name ?? ""} ${item.surname ?? ""}`.trim() })),
		[volunteersExperienceList]
	);

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return search ? rows.filter(x => x.fullName.toLocaleLowerCase("tr-TR").includes(search)) : rows;
	}, [rows, searchText]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearVolunteersExperienceList();
		clearVolunteersExperienceFormData();
		getVolunteersExperienceList();

		return () => {
			clearVolunteersExperienceList();
		};
	}, [clearVolunteersExperienceList, clearVolunteersExperienceFormData, getVolunteersExperienceList]);

	// The order, status and action columns keep the width their headers need; the texts share the rest.
	const columns = useMemo(() => [
		createColumn({
			title: "Sıra Numarası",
			dataIndex: "displayOrder",
			width: 150,
			sorterType: "numeric",
		}),
		createColumn({
			title: "Ad Soyad",
			dataIndex: "fullName",
			sorterType: "string",
		}),
		createColumn({
			title: "Unvan",
			dataIndex: "title",
			sorterType: "string",
		}),
		createColumn({
			title: "Şirket",
			dataIndex: "company",
			sorterType: "string",
		}),
		createColumn({
			title: "Deneyim",
			dataIndex: "description",
			sorterType: "string",
			showFullTextOnHover: false,
		}),
		createColumn({
			title: "Aktiflik Durumu",
			dataIndex: "isActive",
			width: 160,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $bool={record.isActive}>{record.isActive ? "Aktif" : "Pasif"}</StatusCell>
			),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 50,
			render: (text, record) => {
				const item = record as VolunteersExperienceRow;
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`${VOLUNTEERS_EXPERIENCE_LIST_PATH}/edit/${item.id}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(item);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>İyiliği Büyütenler</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Ad Soyad Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate(`${VOLUNTEERS_EXPERIENCE_LIST_PATH}/add`)}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as unknown as DataTypeProps[]}
						rowKey="id"
					/>
				)}
			</Container>
			<Modal
				title="Gönüllü Deneyimini Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeVolunteersExperience(currentItem.id);
							}
							setIsModalVisible(false);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.fullName ?? ""}"`}</strong> adlı gönüllünün deneyimini silmek istediğinizden emin misiniz?</p>
			</Modal>
		</div>
	);
};

export default VolunteersExperienceList;
